import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  Check,
  ArrowRight,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { QuickProductDialog } from '../QuickProductDialog';
import { ImportItemUnitPicker, type ImportLinkUnitInfo } from '../ImportItemUnitPicker';
import { searchProductIdsFuzzy } from '@/lib/productSearch'; // A-quattuor.6 F1-d
import { effectiveItemType, type WizardData, type ProductMapping } from './index';

interface Step4ProductsProps {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData> | ((prev: WizardData) => Partial<WizardData>)) => void;
}

interface XmlItem {
  cProd: string;
  xProd: string;
  NCM: string;
  CFOP: string;
  uCom: string;
  qCom: string;
  vUnCom: string;
  vProd: string;
  cEAN?: string;
}

/** I.151 1b3-b: vínculo do fornecedor com o produto embutido (não depende da lista de 50) */
type SupplierLinkRow = ImportLinkUnitInfo & {
  supplier_product_code: string;
  default_item_type: string | null;
  internal_product: { id: string; name: string } | null;
};

export function Step4Products({ data, onUpdate }: Step4ProductsProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quickProductDialogOpen, setQuickProductDialogOpen] = useState(false);
  const [quickProductItemIndex, setQuickProductItemIndex] = useState<number | null>(null);

  // Get company ID
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const { data: products = [] } = useQuery({
    queryKey: ['products-for-mapping', companyId, searchTerm],
    queryFn: async () => {
      if (!companyId) return [];

      let query = supabase
        .from('products')
        // T4: measurement_unit/selling_price não são colunas de products (42703 — lista vinha vazia);
        // unidade via embed da FK products_measurement_unit_id_fkey
        // F2/T1: sigla exibida = sefaz_code (fallback name); unit_type deixou de ser sigla (S1)
        .select('id, name, sku, ean, sale_price, measurement_unit:measurement_units(sefaz_code, name)')
        .eq('company_id', companyId)
        .eq('active', true)
        .limit(50);

      // A-quattuor.6 F1-d: nome/SKU/EAN via RPC search_products_fuzzy (sem acento, AND por palavra)
      // → ids; o .or() com termo cru saiu.
      if (searchTerm.trim()) {
        const ids = await searchProductIdsFuzzy(companyId, searchTerm);
        query = query.in('id', ids);
      }

      const { data } = await query;
      return (data || []) as any[];
    },
    enabled: !!companyId,
  });

  // I.151 1b3-b (defeito 1): o mapeamento inicial só é montado DEPOIS desta consulta resolver.
  // Query desabilitada (sem CNPJ) nunca fica isFetched no v5 → conta como pronta.
  const linksEnabled = !!companyId && !!data.invoice.emitente_cnpj;
  const linksQuery = useQuery({
    queryKey: ['supplier-product-links', companyId, data.invoice.emitente_cnpj],
    queryFn: async (): Promise<SupplierLinkRow[]> => {
      if (!companyId || !data.invoice.emitente_cnpj) return [];

      const cleanCnpj = data.invoice.emitente_cnpj.replace(/\D/g, '');

      // BUG-P44: Usar RPC para buscar na tabela suppliers (não customers)
      const { data: supplierData } = await supabase.rpc(
        'find_supplier_by_normalized_document',
        { p_company_id: companyId, p_document: cleanCnpj }
      );
      if (!supplierData || supplierData.length === 0) return [];
      const supplierId = supplierData[0].id;

      const { data: links, error } = await supabase
        .from('supplier_product_links')
        .select('*, internal_product:products(id, name, active, deleted_at)')
        .eq('company_id', companyId)
        .eq('supplier_id', supplierId)
        .eq('is_active', true);
      if (error) throw error;

      // Revisor (achado 3): vínculo para produto excluído ou inativo não vincula sozinho
      return ((links || []) as unknown as (SupplierLinkRow & {
        internal_product: { id: string; name: string; active: boolean; deleted_at: string | null } | null;
      })[]).map(l => ({
        ...l,
        internal_product: l.internal_product && l.internal_product.active && !l.internal_product.deleted_at
          ? { id: l.internal_product.id, name: l.internal_product.name }
          : null,
      }));
    },
    enabled: linksEnabled,
  });
  const supplierLinks = linksQuery.data ?? [];
  // Revisor (achado 4): sem empresa ainda não está pronto; sem CNPJ não há vínculo a esperar
  const linksReady = !!companyId && (!data.invoice.emitente_cnpj || linksQuery.isFetched);

  // Extract items from parsed XML
  const xmlItems: XmlItem[] = React.useMemo(() => {
    if (!data.parsedXml?.det) return [];

    const items = Array.isArray(data.parsedXml.det)
      ? data.parsedXml.det
      : [data.parsedXml.det];

    return items.map((item: any) => {
      const prod = item.prod || {};
      return {
        cProd: prod.cProd || '',
        xProd: prod.xProd || '',
        NCM: prod.NCM || '',
        CFOP: prod.CFOP || '',
        uCom: prod.uCom || '',
        qCom: prod.qCom || '',
        vUnCom: prod.vUnCom || '',
        vProd: prod.vProd || '',
        cEAN: prod.cEAN || prod.cEANTrib || '',
      };
    });
  }, [data.parsedXml]);

  // I.151 1b3-b (defeito 2): casamento por EAN numa consulta própria — não na lista de 50
  const eans = useMemo(
    () => Array.from(new Set(xmlItems.map(i => i.cEAN || '').filter(e => e.length >= 8))),
    [xmlItems],
  );
  const eanEnabled = !!companyId && eans.length > 0;
  const eanQuery = useQuery({
    queryKey: ['products-by-ean', companyId, eans],
    queryFn: async (): Promise<{ id: string; name: string; ean: string }[]> => {
      const { data: rows, error } = await supabase
        .from('products')
        .select('id, name, ean')
        .eq('company_id', companyId as string)
        .eq('active', true)
        .is('deleted_at', null)
        .in('ean', eans);
      if (error) throw error;
      return (rows || []) as { id: string; name: string; ean: string }[];
    },
    enabled: eanEnabled,
  });
  const eanReady = !!companyId && (eans.length === 0 || eanQuery.isFetched);
  const lookupFailed = linksQuery.isError || eanQuery.isError;

  const linkByCode = useMemo(() => {
    const m = new Map<string, SupplierLinkRow>();
    for (const l of supplierLinks) m.set(l.supplier_product_code, l);
    return m;
  }, [supplierLinks]);

  useEffect(() => {
    if (xmlItems.length === 0 || data.productMappings.length > 0 || !linksReady || !eanReady) return;
    const eanRows = eanQuery.data ?? [];
    const initialMappings: ProductMapping[] = xmlItems.map((item) => {
      const link = linkByCode.get(item.cProd) ?? null;
      const base: ProductMapping = {
        xmlItem: item,
        productId: null,
        productName: item.xProd,
        quantity: parseFloat(item.qCom),
        unitPrice: parseFloat(item.vUnCom),
        total: parseFloat(item.vProd),
        cfop: item.CFOP,
        productUnitId: undefined,
        link,
      };

      // Vínculo do fornecedor: o produto vem do próprio vínculo (embed), esteja onde estiver
      if (link?.internal_product) {
        return {
          ...base,
          productId: link.internal_product.id,
          productName: link.internal_product.name,
          // A-tric.10: sugestão do link — só 'internal_use' sobrescreve (produto do link
          // PRESERVADO, o motor curado não movimenta); demais valores herdam da natureza
          ...(link.default_item_type === 'internal_use' ? { itemType: 'internal_use' as const } : {}),
        };
      }

      // Try to match by EAN
      if (item.cEAN && item.cEAN.length >= 8) {
        const matchedProduct = eanRows.find((p) => p.ean === item.cEAN);
        if (matchedProduct) {
          return { ...base, productId: matchedProduct.id, productName: matchedProduct.name };
        }
      }

      // No match found
      return base;
    });

    onUpdate({ productMappings: initialMappings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xmlItems, linksReady, eanReady, linkByCode, eanQuery.data]);

  const handleSelectProduct = (itemIndex: number, productId: string, productName: string) => {
    const updatedMappings = [...data.productMappings];
    const cProd = (updatedMappings[itemIndex].xmlItem as XmlItem).cProd;
    updatedMappings[itemIndex] = {
      ...updatedMappings[itemIndex],
      productId,
      productName,
      // I.151 1b3-b: produto novo = unidade a resolver de novo (o seletor decide)
      productUnitId: undefined,
      unitLabel: undefined,
      link: linkByCode.get(cProd) ?? null,
    };

    onUpdate({ productMappings: updatedMappings });
  };

  const getMappingStatus = (mapping: ProductMapping) => {
    if (!mapping.productId) return 'pending';
    if (mapping.productUnitId === undefined) return 'unit';
    return 'matched';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'unit':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const mappedCount = data.productMappings.filter(m => m.productId).length;
  // A-tric.10: uso interno sem cadastro é legítimo (vai ao payload com product_id NULL)
  const usoInternoSemCadastro = data.productMappings.filter(
    m => !m.productId && effectiveItemType(m, data.nature) === 'internal_use',
  ).length;
  // I.151 1b3-b: item que movimenta estoque com unidade indefinida bloqueia o avanço
  const semUnidade = data.productMappings.filter(
    m => !!m.productId && effectiveItemType(m, data.nature) !== 'internal_use' && m.productUnitId === undefined,
  ).length;

  if (!linksReady || !eanReady) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Carregando vínculos do fornecedor...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">Vincular Produtos</h3>
        <p className="text-sm text-muted-foreground">
          Associe cada produto da nota a um produto do seu cadastro e confira a unidade
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
        <span className="text-sm">
          Produtos vinculados: <strong>{mappedCount}</strong> de <strong>{xmlItems.length}</strong>
          {usoInternoSemCadastro > 0 && (
            <span className="text-muted-foreground"> · {usoInternoSemCadastro} uso interno sem cadastro</span>
          )}
          {semUnidade > 0 && (
            <span className="text-amber-700"> · {semUnidade} sem unidade definida</span>
          )}
        </span>
        {mappedCount === xmlItems.length && semUnidade === 0 && (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Check className="h-3 w-3 mr-1" />
            Todos vinculados
          </Badge>
        )}
      </div>

      {lookupFailed && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Não foi possível carregar os vínculos do fornecedor ou a busca por código de barras. Vincule os itens à mão
          ou feche e abra a nota de novo.
        </div>
      )}

      {/* Product List */}
      <ScrollArea className="h-[400px] pr-4">
        <div className="space-y-4">
          {data.productMappings.map((mapping, index) => {
            const status = getMappingStatus(mapping);
            const xmlItem = mapping.xmlItem as XmlItem;
            // A-tric.8: classificação explícita por item (sobrescrita ou herdada da natureza)
            const itemType = effectiveItemType(mapping, data.nature);
            const setItemType = (novo: 'resale' | 'internal_use') => {
              const updated = [...data.productMappings];
              updated[index] = { ...updated[index], itemType: novo }; // productId preservado (rastro)
              onUpdate({ productMappings: updated });
            };

            return (
              <Card key={index} className="overflow-hidden">
                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x">
                  {/* XML Item Side */}
                  <div className="p-4 bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={getStatusColor(status)}>
                          {status === 'matched' && 'Vinculado'}
                          {status === 'unit' && 'Definir unidade'}
                          {status === 'pending' && 'Pendente'}
                        </Badge>
                        <Badge variant={itemType === 'internal_use' ? 'secondary' : 'outline'} className="text-[10px]">
                          {itemType === 'internal_use' ? 'Uso Interno' : 'Revenda'}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">Item {index + 1}</span>
                    </div>

                    <h4 className="font-medium text-sm mb-2 line-clamp-2">
                      {xmlItem.xProd}
                    </h4>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Código:</span>
                        <span className="ml-1 font-mono">{xmlItem.cProd}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">NCM:</span>
                        <span className="ml-1 font-mono">{xmlItem.NCM}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Qtd:</span>
                        <span className="ml-1 font-medium">{xmlItem.qCom} {xmlItem.uCom}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Valor:</span>
                        <span className="ml-1 font-medium">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                            .format(parseFloat(xmlItem.vProd))}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Product Selection Side */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <ArrowRight className="h-4 w-4 text-muted-foreground hidden md:block" />
                      <span className="text-xs text-muted-foreground">Produto no Sistema</span>
                    </div>

                    {mapping.productId ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-sm">{mapping.productName}</span>
                        </div>

                        {itemType !== 'internal_use' && (
                          <ImportItemUnitPicker
                            productId={mapping.productId}
                            uCom={xmlItem.uCom}
                            quantity={mapping.quantity}
                            productUnitId={mapping.productUnitId}
                            link={mapping.link}
                            onChange={(choice) => {
                              // Revisor (achado 5): atualização funcional — dois seletores que se
                              // resolvem na mesma renderização não se sobrescrevem
                              onUpdate(prev => ({
                                productMappings: prev.productMappings.map((m, i) =>
                                  i === index ? { ...m, productUnitId: choice.productUnitId, unitLabel: choice.unitLabel } : m
                                ),
                              }));
                            }}
                          />
                        )}

                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = [...data.productMappings];
                              updated[index] = { ...updated[index], productId: null, productUnitId: undefined, unitLabel: undefined };
                              onUpdate({ productMappings: updated });
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Alterar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Buscar produto..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setSelectedItemIndex(index)}
                          />
                        </div>

                        {selectedItemIndex === index && products.length > 0 && (
                          <div className="max-h-32 overflow-y-auto border rounded-md">
                            {products.map((product) => (
                              <button
                                key={product.id}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between items-center"
                                onClick={() => {
                                  handleSelectProduct(index, product.id, product.name);
                                  setSearchTerm('');
                                }}
                              >
                                <div>
                                  <div className="font-medium">{product.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    SKU: {product.sku || '-'} | {product.measurement_unit?.sefaz_code || product.measurement_unit?.name || 'UN'}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setQuickProductItemIndex(index);
                            setQuickProductDialogOpen(true);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Criar novo produto
                        </Button>
                      </div>
                    )}

                    {/* R-NF.9 (paridade F0): troca Revenda | Uso Interno por item, COM ou SEM produto
                        (molde ImportStep3Products) — grava itemType, nunca zera o produto */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant={itemType === 'resale' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setItemType('resale')}
                      >
                        Revenda
                      </Button>
                      <Button
                        variant={itemType === 'internal_use' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setItemType('internal_use')}
                      >
                        Uso Interno
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Uso interno não movimenta estoque</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {/* Quick Product Dialog — I.151 1b3-b: não ensina apelido de unidade (embalagem vira
          unidade alternativa do produto pelo seletor de unidade) */}
      <QuickProductDialog
        open={quickProductDialogOpen}
        onOpenChange={setQuickProductDialogOpen}
        suppressUnitAlias
        onProductCreated={(product) => {
          if (quickProductItemIndex !== null) {
            handleSelectProduct(quickProductItemIndex, product.id, product.name);
            // Invalidate products query to refresh the list
            queryClient.invalidateQueries({ queryKey: ['products-for-mapping'] });
          }
          setQuickProductDialogOpen(false);
          setQuickProductItemIndex(null);
        }}
        defaultValues={
          quickProductItemIndex !== null && data.productMappings[quickProductItemIndex]
            ? {
                name: (data.productMappings[quickProductItemIndex].xmlItem as XmlItem).xProd,
                sku: (data.productMappings[quickProductItemIndex].xmlItem as XmlItem).cProd,
                ncm: (data.productMappings[quickProductItemIndex].xmlItem as XmlItem).NCM,
                ean: (data.productMappings[quickProductItemIndex].xmlItem as XmlItem).cEAN,
                unit: (data.productMappings[quickProductItemIndex].xmlItem as XmlItem).uCom,
                unitPrice: parseFloat((data.productMappings[quickProductItemIndex].xmlItem as XmlItem).vUnCom) || 0,
              }
            : undefined
        }
      />
    </div>
  );
}
